// AWARDS (§9.5, §9.8, §11.5): Play of the Match first (the Renderer's VHS replay; this screen adds
// its billing and skip), then up to 6 joke awards revealed one by one — a drumroll, a 3D card flip
// showing the award, the bean's rendered portrait, its name and value — with confetti in the
// winner's identity color. The reveal is paced to the phase: the last card lands with time to read
// it before the Podium (AWARDS is 480 ticks, +240 with Play of the Match). Skipping the replay is
// local (straight to the cards); once the cards are out, Skip is the room's collective AWARDS skip.
import { MAX_BEANS, TICK_HZ } from '@shared/constants';
import { RoomPhase, type AwardId } from '@shared/ids';
import type { MatchEndDTO, PlayerDTO } from '@shared/protocol/messages';
import { confetti } from '../anim';
import type { LiveScreen, NavAction } from '../contracts';
import type { UiDeps } from '../deps';
import { actionCap } from '../glyphView';
import { AWARD_ICONS, beanIcon, icon, identityBadge, identityHex } from '../icons';
import { AWARD_BLURBS, AWARD_NAMES, awardValueText } from '../labels';
import { el, setHidden, setText, toggleClass } from '../ui';
import { button } from '../widgets';
import { replayOverlay, skipTally } from './ReplayOverlay';

const DRUMROLL_MS = 650;
/** Pause between card flips when the phase has room for it… */
const FLIP_GAP_MS = 1150;
/** …and the quickest pace when it does not (a late Play of the Match, a slow join). */
const MIN_FLIP_GAP_MS = 600;
/** The last card stays up at least this long before the Podium. */
const READ_LAST_MS = 2400;

export function create(deps: UiDeps): LiveScreen {
  const room = deps.room;
  const grid = el('div', { class: 'award-grid' });
  const sub = el('div', { class: 'awards-sub' });
  const head = el('div', { class: 'awards-head' }, el('div', { class: 'awards-title inked', text: 'AWARDS' }), sub);
  const stage = el('div', { class: 'awards-stage' }, head, grid);
  const replay = replayOverlay(deps, ['potm']);
  const skipCap = el('span', { class: 'vhs-cap' });
  const skipText = el('span');
  const skipPill = el('button', { class: 'awards-skip', attrs: { type: 'button' }, on: { click: () => skipToPodium() } }, skipCap, skipText);
  const menuBtn = button({ icon: 'pause', title: 'Menu', variant: 'cream', size: 'small', cls: 'round screen-menu', onClick: () => deps.nav.show('pause') });
  const root = el('div', { class: 'screen modal awards-screen' }, el('div', { class: 'sunburst' }), stage, skipPill, replay.el, menuBtn);
  let timers: number[] = [];
  let builtFor: MatchEndDTO | null = null;
  let revealStarted = false;
  let skipSent = false;
  let skipKey = '';

  function clear(): void {
    for (const t of timers) clearTimeout(t);
    timers = [];
  }

  function card(a: { id: AwardId; slot: number; value: number }, p: PlayerDTO | null, i: number): HTMLElement {
    const colorblind = deps.settings().accessibility.colorblind;
    const hex = identityHex(p?.identity ?? a.slot, colorblind);
    const portrait = p ? deps.services.portrait(p.cosmetics, p.identity, 160) : null;
    const front = el('div', { class: 'ac-face ac-front' },
      el('div', { class: 'ac-ribbon', text: AWARD_NAMES[a.id] }),
      el('div', { class: 'ac-art', style: { '--id': hex } }, portrait ?? beanIcon(hex), el('span', { class: 'ac-icon' }, icon(AWARD_ICONS[a.id]))),
      el('div', { class: 'ac-who' }, p ? identityBadge(p.identity, colorblind) : null, el('b', { class: 'ellipsis', text: p?.name ?? `Bean ${a.slot + 1}` })),
      el('div', { class: 'ac-value', text: awardValueText(a.id, a.value) }),
      el('div', { class: 'ac-blurb', text: AWARD_BLURBS[a.id] }));
    const back = el('div', { class: 'ac-face ac-back' }, el('span', { class: 'ac-q', text: '?' }));
    const c = el('div', { class: 'award-card', attrs: { 'data-slot': a.slot, 'aria-label': `${AWARD_NAMES[a.id]}: ${p?.name ?? ''}` } },
      el('div', { class: 'ac-inner' }, back, front));
    c.style.setProperty('--i', String(i));
    c.style.setProperty('--id', hex);
    return c;
  }

  function build(end: MatchEndDTO): void {
    clear();
    builtFor = end;
    revealStarted = false;
    const players = room.dto?.players ?? [];
    const awards = end.awards.slice(0, 6);
    setText(sub, awards.length === 0 ? 'Nobody did anything silly enough this time!' : `${awards.length} award${awards.length === 1 ? '' : 's'} up for grabs`);
    grid.replaceChildren(...awards.map((a, i) => card(a, a.slot < MAX_BEANS ? players[a.slot] ?? null : null, i)));
    toggleClass(grid, 'few', awards.length <= 3);
    // Up to 5 cards fit one row (a lone 4th card on a second row looked unfinished); 6 make 3 × 2.
    grid.style.setProperty('--cols', String(awards.length <= 5 ? Math.max(1, awards.length) : 3));
  }

  /** The flip pace that lands the last card READ_LAST_MS before the phase ends. */
  function flipGap(cards: number): number {
    const dto = room.dto;
    if (!dto || cards <= 1) return FLIP_GAP_MS;
    const leftMs = ((dto.phaseEndTick - room.roomTick()) / TICK_HZ) * 1000;
    const fit = (leftMs - READ_LAST_MS - DRUMROLL_MS) / (cards - 1);
    return Math.max(MIN_FLIP_GAP_MS, Math.min(FLIP_GAP_MS, fit));
  }

  function reveal(): void {
    if (revealStarted) return;
    revealStarted = true;
    const cards = [...grid.querySelectorAll<HTMLElement>('.award-card')];
    const gap = flipGap(cards.length);
    const drum = Math.min(DRUMROLL_MS, gap * 0.6);
    cards.forEach((c, i) => {
      timers.push(window.setTimeout(() => {
        c.classList.add('drumroll');
        deps.services.playUi('drumroll');
      }, i * gap));
      timers.push(window.setTimeout(() => {
        c.classList.remove('drumroll');
        c.classList.add('flipped');
        deps.services.playUi('cardFlip');
        const r = c.getBoundingClientRect();
        const host = root.getBoundingClientRect();
        const hex = c.style.getPropertyValue('--id') || '#FFD24A';
        confetti(root, r.left - host.left + r.width / 2, r.top - host.top + r.height * 0.3, [hex, '#FFFFFF', '#FFD24A', '#FF4F9A'], 36, 0.9);
        if (Number(c.dataset.slot) === room.slot) c.classList.add('mine');
      }, i * gap + drum));
    });
  }

  /** The room's AWARDS skip (§9.5, collective): on to the Podium once every human pressed it. */
  function skipToPodium(): void {
    if (skipSent || !revealStarted || room.spectator) return;
    skipSent = true;
    deps.services.playUi('click');
    deps.actions.send({ t: 'skip' });
  }

  function renderSkip(): void {
    const show = revealStarted && !room.spectator && room.phase === RoomPhase.AWARDS;
    setHidden(skipPill, !show);
    if (!show) return;
    const dev = deps.services.input.device;
    const t = skipTally(deps);
    const key = `${dev}|${skipSent}|${t.humans}|${t.skipped}`;
    if (key === skipKey) return;
    skipKey = key;
    skipCap.replaceChildren(...(dev === 'touch' ? [] : [actionCap(deps, 'confirm')]));
    const count = t.humans > 1 ? ` ${Math.max(t.skipped, skipSent ? 1 : 0)}/${t.humans}` : '';
    setText(skipText, skipSent ? (t.humans > 1 ? `Ready for the podium ✓${count}` : 'On to the podium…') : `${dev === 'touch' ? 'Tap: ' : ''}skip to the podium${t.skipped > 0 ? ` •${count}` : ''}`);
    toggleClass(skipPill, 'done', skipSent);
  }

  function render(): void {
    const end = room.matchEnd;
    if (end && end !== builtFor) build(end);
  }
  room.onChange(() => {
    if (!root.hidden) render();
  });

  return {
    el: root,
    onShow() {
      builtFor = null;
      skipSent = false;
      skipKey = '';
      render();
    },
    onHide() {
      clear();
      revealStarted = false;
    },
    onNav(action: NavAction) {
      if (replay.active()) {
        if (action === 'confirm') replay.skip();
        return action !== 'pause';
      }
      if (action === 'confirm' && revealStarted && !(document.activeElement instanceof HTMLElement && root.contains(document.activeElement) && document.activeElement !== skipPill)) {
        skipToPodium();
        return true;
      }
      return false;
    },
    update() {
      const replaying = replay.update();
      toggleClass(root, 'replaying', replaying);
      if (!replaying && room.phase === RoomPhase.AWARDS && builtFor) reveal();
      renderSkip();
    },
  };
}
